import { h } from '@omni-torn/ui';
import { BOT_SERVER_TERMS, BOT_SERVER_TERMS_VERSION, isSnowflake } from '@omni-torn/shared';
import { server, session } from '../session';
import { box } from '../ui';

/**
 * /bot — the Omni-Torn Discord bot, for everyone: no sign-in (public bot Phase B, B1). What it does, **Add to Discord**
 * with Basic or Full permissions (the links come from the bot through GET /api/bot/invite), how setup and approval work,
 * and what the bot keeps about a server — the bot's server terms (packages/shared bot-servers.ts), which a server's admin
 * agrees to at the end of `/omni setup` (anchor #server-terms; the bot links here).
 */

const PARTS: Array<[string, string]> = [
  ['Faction alerts', 'Enemy pushes and travel in wars, retaliations, the chain timer, hospital and travel boards, OC delays and pass rates, members joining or leaving — posted in the channels you pick, read with your faction members’ own keys.'],
  ['Giveaways', 'Run giveaways in Discord or start them from Torn, with entry checks and prize proofs.'],
  ['Banking', '/withdraw and /balance: vault requests your bankers answer.'],
  ['Stakeouts', 'Watch a player or a faction: hospital in and out, flying, landing, coming online, members joining or leaving.'],
  ['Linking and verification', 'Members link through Torn’s own Discord link (or their own key); roles by link, faction and position; Torn-style nicknames.'],
];

type Invite = { available: boolean; basic?: string; full?: string };

export function renderBot(view: HTMLElement): void {
  document.title = 'Discord bot · Omni-Torn';
  const links = h('div', { class: 'bot-invites', 'data-field': 'invites' }, h('p', { class: 'muted' }, 'Loading the invite links…'));
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Omni-Torn for Discord')),
    box(
      'What it does',
      [
        h('p', {}, 'Torn tools for your Discord server — a faction’s own server, a server of several factions, or a community with no faction. Everything starts switched off; your admins pick the parts.'),
        h('ul', { class: 'plain-list bot-parts' }, ...PARTS.map(([name, what]) => h('li', {}, h('strong', {}, name), ': ', what))),
      ],
      { id: 'bot-what' },
    ),
    box(
      'Add it to your server',
      [
        h('p', { class: 'notice', 'data-field': 'invite-only' }, h('strong', {}, 'Invite-only for now. '), 'Any server can add the bot, but its parts switch on only once Omni-Torn approves the server. Linking and ', h('code', {}, '/account'), ' work meanwhile.'),
        links,
        h(
          'ol',
          { class: 'bot-steps' },
          h('li', {}, 'Pick ', h('strong', {}, 'Basic'), ' (alerts, giveaways, banking, stakeouts) or ', h('strong', {}, 'Full'), ' (also verification roles and nicknames — the bot can then hand out roles and rename members). You can switch later with ', h('code', {}, '/omni invite'), '.'),
          h('li', {}, 'A server admin presses ', h('strong', {}, 'Set up'), ' on the bot’s hello (or runs ', h('code', {}, '/omni setup'), '): what kind of server, the admin’s Torn key, the faction, channels, admin roles and parts.'),
          h('li', {}, 'The key is ', h('strong', {}, 'the same key you sign in with on the extension, the website and Torn PDA'), ' — one key for all. Torn’s terms table is shown before it’s asked for (', h('a', { href: '/terms' }, 'Omni-Torn’s terms'), ').'),
          h('li', {}, 'Last, the admin agrees to the bot’s server terms (below). The parts start once Omni-Torn has approved the server.'),
          h('li', {}, 'For roles and nicknames: drag the ', h('strong', {}, 'Omni-Torn'), ' role above the roles it hands out (Server Settings → Roles). ', h('code', {}, '/omni check'), ' says what’s missing.'),
        ),
      ],
      { id: 'bot-add' },
    ),
    box(
      h('span', { id: 'server-terms' }, 'Server owners: what we keep'),
      [
        h('p', { class: 'muted', 'data-field': 'server-terms-version' }, `The bot’s server terms, version ${BOT_SERVER_TERMS_VERSION} — a server admin agrees to them for the server at the end of setup.`),
        h('dl', { class: 'bot-terms' }, ...BOT_SERVER_TERMS.flatMap((r) => [h('dt', { id: r.id }, r.name), h('dd', {}, r.text)])),
        h('p', {}, h('a', { href: '/terms' }, 'Omni-Torn’s terms (Torn’s table, for keys)'), ' · ', h('a', { href: '/privacy.html' }, 'Privacy policy'), ' · ', session.get() ? h('a', { href: '/account', 'data-link': true }, 'Your account') : h('a', { href: '/', 'data-link': true }, 'Sign in')),
      ],
      { id: 'server-terms' },
    ),
    reportServer(),
  );
  void fetch('/api/bot/invite', { headers: { accept: 'application/json' } })
    .then((r): Invite | Promise<Invite> => (r.ok ? (r.json() as Promise<Invite>) : { available: false }))
    .catch((): Invite => ({ available: false }))
    .then((inv: Invite) => {
      if (!inv.available || !inv.basic || !inv.full) {
        links.replaceChildren(h('p', { class: 'notice bad', 'data-field': 'invite-missing' }, 'The bot isn’t reachable right now, so its invite links can’t be shown — try again in a few minutes.'));
        return;
      }
      links.replaceChildren(
        h('a', { class: 'btn-primary bot-add', href: inv.basic, rel: 'noopener', target: '_blank', 'data-invite': 'basic' }, 'Add to Discord — Basic'),
        h('a', { class: 'btn-small bot-add', href: inv.full, rel: 'noopener', target: '_blank', 'data-invite': 'full' }, 'Add to Discord — Full'),
      );
    });
  // Opened at #server-terms (the bot's setup links here): the page is drawn after the browser looked for the anchor.
  const id = decodeURIComponent(location.hash.slice(1));
  if (id) document.getElementById(id)?.scrollIntoView();
}

/**
 * "Report this server" (public bot G1): a Discord server misusing the bot (scam giveaways, posing as Omni-Torn, breaking
 * Torn's or Discord's rules) — sent as a ticket to Omni-Torn's owner, like any report (POST /api/bot/report-server, signed
 * in). He can block the server on the Owner tab: the bot's parts go off there.
 */
function reportServer(): HTMLElement {
  const body = h('div', { class: 'report-server', 'data-field': 'report-server' });
  if (!session.get()) {
    body.append(h('p', {}, 'A server misusing the bot (scam giveaways, posing as Omni-Torn, breaking Torn’s or Discord’s rules)? ', h('a', { href: '/', 'data-link': true }, 'Sign in'), ' to report it — it becomes a ticket for Omni-Torn’s owner.'));
  } else {
    const id = h('input', { type: 'text', inputmode: 'numeric', maxlength: '21', placeholder: 'Discord server id', 'aria-label': 'Discord server id', 'data-field': 'report-id' }) as HTMLInputElement;
    const text = h('textarea', { maxlength: '2000', placeholder: 'What’s wrong (what you saw, when, a message link)', 'aria-label': 'What’s wrong', 'data-field': 'report-text' }) as HTMLTextAreaElement;
    const status = h('span', { class: 'save-status', role: 'status', 'data-field': 'report-status' });
    const send = h('button', { type: 'button', class: 'btn-small', 'data-action': 'report-server' }, 'Send the report') as HTMLButtonElement;
    send.addEventListener('click', () => {
      if (!isSnowflake(id.value.trim())) return void (status.textContent = 'A Discord server id is 15–21 digits (Discord: right-click the server → Copy Server ID, with Developer Mode on).');
      if (text.value.trim().length < 5) return void (status.textContent = 'Say what’s wrong.');
      send.disabled = true;
      status.textContent = 'Sending…';
      void server<{ id: string }>('POST', '/api/bot/report-server', { server: id.value.trim(), text: text.value.trim() })
        .then((r) => {
          status.textContent = `Sent — ticket ${r.id}. You’ll find it under Account → Your reports.`;
          text.value = '';
        })
        .catch((err: unknown) => (status.textContent = (err as Error).message))
        .finally(() => (send.disabled = false));
    });
    body.append(h('p', { class: 'muted' }, 'A server misusing the bot (scam giveaways, posing as Omni-Torn, breaking Torn’s or Discord’s rules)? Tell Omni-Torn’s owner — it becomes a ticket.'), id, text, h('div', {}, send, ' ', status));
  }
  return box(h('span', { id: 'report-server' }, 'Report a server'), body, { id: 'report-server' });
}
